// components/trackProvenance.js
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import Web3 from "web3";
import { contractABI, contractAddress } from "../contractConfig";
import "../tokenDetails.css";

export default function TrackProvenance() {
  const { tokenId } = useParams();

  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);

  // RPC for read-only chain access
  const infuraKey = process.env.REACT_APP_INFURA_API_KEY;
  const web3 = useMemo(() => {
    const rpc = infuraKey
      ? `https://polygon-amoy.infura.io/v3/${infuraKey}`
      : "https://rpc-amoy.polygon.technology";
    return new Web3(rpc);
  }, [infuraKey]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const { data } = await axios.get(`${apiUrl}/api/tokens/${tokenId}`);
        if (!cancelled) setToken(data);
      } catch {
        if (!cancelled) setError("Failed to load token");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tokenId]);

  const fetchTokenFromBlockchain = async (id) => {
    try {
      const contract = new web3.eth.Contract(contractABI, contractAddress);
      return await contract.methods.getTokenEvents(id).call();
    } catch (err) {
      console.error("Error fetching on-chain events:", err);
      return null;
    }
  };

  const verifyTokenData = (backendToken, blockchainEvents) => {
    if (!Array.isArray(blockchainEvents)) return false;
    return blockchainEvents.some(
      (e) => e?.metadataHash === backendToken?.metadataHash && e?.signature === backendToken?.signature
    );
  };

  const handleVerifyToken = async () => {
    if (!token) return;
    setIsVerifying(true);
    try {
      const onchain = await fetchTokenFromBlockchain(token.tokenId);
      if (!onchain || onchain.length === 0) {
        setVerificationStatus("No on-chain events found for this token");
        return;
      }
      const ok = verifyTokenData(token, onchain);
      setVerificationStatus(ok ? "Verified with Blockchain" : "Mismatch with Blockchain");
    } catch {
      setVerificationStatus("Error verifying token");
    } finally {
      setIsVerifying(false);
    }
  };

  if (loading) return <p>Loading…</p>;
  if (error) return <p style={{ color: "red" }}>{error}</p>;
  if (!token) return null;

  return (
    <div className="container">
      <h2>ApToken #{token.tokenId}</h2>

      <div className="token-details-box">
        <p><strong>Name:</strong> {token.name}</p>
        <p><strong>Description:</strong> {token.description}</p>
        <p><strong>URL:</strong> <a href={token.url} target="_blank" rel="noreferrer">{token.url}</a></p>
        <p><strong>Metadata Hash:</strong> {token.metadataHash}</p>
        <p><strong>Signature:</strong> {token.signature}</p>
        <p><strong>Transaction Hash:</strong> {token.transactionHash}</p>
        <p><strong>Timestamp:</strong> {new Date(token.timestamp).toLocaleString()}</p>

        <button onClick={handleVerifyToken} disabled={isVerifying}>
          {isVerifying ? "Verifying..." : "Verify with Blockchain"}
        </button>
        {verificationStatus && (
          <p style={{ color: verificationStatus.startsWith("Verified") ? "green" : "red", marginTop: 8 }}>
            {verificationStatus}
          </p>
        )}
      </div>

      <h3>Provenance Events</h3>
      {Array.isArray(token.events) && token.events.length ? (
        <div className="provenance-events-box">
          {token.events.map((e, i) => (
            <div key={i} className="event-item">
              <h4>Event {i + 1}: {e.eventName}</h4>
              <p><strong>Description:</strong> {e.eventDescription}</p>
              <p><strong>Location:</strong> {e.eventAddress}</p>
              <p><strong>Event Hash:</strong> {e.eventHash}</p>
              <p><strong>Signature:</strong> {e.signature}</p>
              <p><strong>File:</strong> <a href={e.file} target="_blank" rel="noreferrer">{e.file}</a></p>
              <p><strong>Transaction Hash:</strong> {e.transactionHash}</p>
              <p><strong>Timestamp:</strong> {new Date(e.timestamp).toLocaleString()}</p>
            </div>
          ))}
        </div>
      ) : <p>No events yet.</p>}
    </div>
  );
}